import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight, RefreshCw, CheckCircle, XCircle, AlertCircle,
  Link2, Calendar, Eye, EyeOff, Download, Settings,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import { BrandGlyph } from './IntegrationsPage'
import type { HaileyStatus, HaileyPerson, SyncLogEntry } from '@/types/api'
import React from 'react'

const GLYPH = {
  monogram: 'HR',
  gradient: 'linear-gradient(135deg, oklch(0.62 0.18 285) 0%, oklch(0.5 0.2 290) 100%)',
}

type Tab = 'employees' | 'log' | 'mapping'

export default function HaileyPage() {
  const queryClient = useQueryClient()
  const [tab,     setTab]     = useState<Tab>('employees')
  const [apiKey,  setApiKey]  = useState('')
  const [showKey, setShowKey] = useState(false)
  const [search,  setSearch]  = useState('')

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['hailey', 'status'],
    queryFn:  () => api.get<HaileyStatus>('/hailey/status'),
  })
  const { data: people = [] } = useQuery({
    queryKey: ['hailey', 'people'],
    queryFn:  () => api.get<HaileyPerson[]>('/hailey/people'),
    enabled:  status?.connected === true,
    staleTime: 5 * 60 * 1000,
  })
  const { data: syncLog = [] } = useQuery({
    queryKey: ['hailey', 'sync-log'],
    queryFn:  () => api.get<SyncLogEntry[]>('/hailey/sync-log'),
    enabled:  status?.connected === true,
  })

  const configureMutation = useMutation({
    mutationFn: (key: string) => api.post<unknown>('/hailey/configure', { apiKey: key }),
    onSuccess:  () => {
      setApiKey('')
      queryClient.invalidateQueries({ queryKey: ['hailey'] })
    },
  })
  const disconnectMutation = useMutation({
    mutationFn: () => api.delete<unknown>('/hailey/connect'),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['hailey'] }),
  })
  const syncMutation = useMutation({
    mutationFn: () => api.post<unknown>('/hailey/sync', {}),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['hailey'] }),
  })

  const handleDisconnect = () => {
    if (!confirm('Disconnect Hailey HR? The sync will stop and the API key will be deleted.')) return
    disconnectMutation.mutate()
  }

  if (statusLoading) {
    return <div className="page"><Breadcrumb /><p className="muted" style={{ paddingTop: 32, textAlign: 'center' }}>Loading…</p></div>
  }

  // ── Not connected ──────────────────────────────────────────────────────────
  if (!status?.connected) {
    return (
      <div className="page">
        <Breadcrumb />
        <div className="card" style={{ maxWidth: 520, padding: 32, textAlign: 'center' }}>
          <div style={{ margin: '0 auto 16px', width: 48 }}><BrandGlyph {...GLYPH} size={48} /></div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Connect Hailey HR</h2>
          <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, marginBottom: 24 }}>
            Sync employees from Hailey HR to populate the People directory in Forge.
            Enter your Hailey API key below to get started.
          </p>
          <div style={{ background: 'var(--ns-surface-2)', borderRadius: 8, padding: 16, marginBottom: 24, textAlign: 'left' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Before you connect</div>
            <ol style={{ fontSize: 13, lineHeight: 1.8, paddingLeft: 18 }}>
              <li>Contact Hailey support to request an API key</li>
              <li>Ensure the key has read access to Employees and Time Off</li>
              <li>Paste the key below — it is stored securely in the database</li>
            </ol>
          </div>
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <input
              type={showKey ? 'text' : 'password'}
              className="input"
              placeholder="Paste API key…"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              style={{ width: '100%', paddingRight: 40, fontFamily: 'JetBrains Mono', fontSize: 13 }}
            />
            <button className="btn btn-ghost btn-icon btn-sm"
              onClick={() => setShowKey(v => !v)} type="button"
              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)' }}>
              {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <button className="btn btn-brand" style={{ gap: 8, width: '100%', justifyContent: 'center', height: 40 }}
            onClick={() => configureMutation.mutate(apiKey)}
            disabled={!apiKey.trim() || configureMutation.isPending}>
            {configureMutation.isPending
              ? <><RefreshCw size={14} style={{ animation: 'spin 0.8s linear infinite' }} />Connecting…</>
              : 'Connect Hailey HR'
            }
          </button>
          {configureMutation.isError && (
            <p style={{ color: 'var(--ns-err)', fontSize: 12.5, marginTop: 10 }}>
              Failed to connect — check the API key and try again.
            </p>
          )}
        </div>
      </div>
    )
  }

  // ── Connected ──────────────────────────────────────────────────────────────
  const lastSyncTime = status.lastSync ? new Date(status.lastSync).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : null
  const lastSyncDate = status.lastSync ? new Date(status.lastSync).toLocaleDateString('sv-SE') : null

  const filtered = people.filter(p =>
    `${p.firstName} ${p.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
    (p.email ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (p.department ?? '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="page">
      <Breadcrumb />

      {/* Hero header */}
      <div className="card" style={{
        padding: 24, marginBottom: 20,
        background: 'linear-gradient(135deg, var(--ns-surface) 0%, var(--ns-surface-2) 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'radial-gradient(circle, var(--ns-brand-soft) 0%, transparent 70%)', opacity: 0.6, pointerEvents: 'none' }} />
        <div className="row" style={{ gap: 18, alignItems: 'flex-start', position: 'relative' }}>
          <BrandGlyph {...GLYPH} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 4 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}>Hailey HR</h1>
              <span className="badge badge-ok"><span className="dot dot-ok" />Connected</span>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
              Employee master data and people directory. Synced every 30 min.
            </p>
            <div className="row" style={{ gap: 12, marginTop: 12, fontSize: 11.5, color: 'var(--ns-text-muted)' }}>
              <span className="row" style={{ gap: 5 }}><Link2 size={12} />api.haileyhr.com</span>
              {status.lastSync && <>
                <span style={{ width: 1, height: 12, background: 'var(--ns-border)' }} />
                <span className="row" style={{ gap: 5 }}><Calendar size={12} />Synced {lastSyncDate}</span>
              </>}
            </div>
          </div>
          <div className="row" style={{ gap: 6, flexShrink: 0 }}>
            <button className="btn btn-ghost btn-sm" style={{ gap: 5 }}><Settings size={13} />Configure</button>
            <button className="btn btn-ghost btn-sm" style={{ gap: 5, color: 'var(--ns-err)' }}
              onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
              <Link2 size={13} />Disconnect
            </button>
            <button className="btn btn-brand btn-sm" style={{ gap: 5 }}
              onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
              <RefreshCw size={13} style={{ animation: syncMutation.isPending ? 'spin 0.8s linear infinite' : undefined }} />
              {syncMutation.isPending ? 'Syncing…' : 'Sync now'}
            </button>
          </div>
        </div>
      </div>

      {/* Stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 22 }}>
        <DetailStat label="Active employees" value={String(status.employeeCount)} sub="Synced from Hailey" trend={[1,2,2,3,3,3,4]} />
        <DetailStat label="Total synced"     value={String(people.length)}        sub={`of ${people.length} in Hailey`} />
        <DetailStat label="Last sync"        value={lastSyncTime ?? '—'}           sub={lastSyncDate ?? 'Never'} />
        <DetailStat label="Sync status" isCustom
          value={<span className="badge badge-ok" style={{ fontSize: 13, height: 26, padding: '0 10px' }}><span className="dot dot-ok" />Healthy</span>}
          sub="Every 30 min" />
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 0 }}>
        {([
          ['employees', 'Employees',     status.employeeCount],
          ['log',       'Sync log',      null],
          ['mapping',   'Field mapping', null],
        ] as [Tab, string, number | null][]).map(([id, label, count]) => (
          <button key={id} className={`tab${tab === id ? ' active' : ''}`}
            onClick={() => { setTab(id); setSearch('') }}>
            {label}{count !== null && <span className="count">{count}</span>}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 18 }}>
        {tab === 'employees' && <EmployeesTab rows={filtered} search={search} onSearch={setSearch} total={people.length} />}
        {tab === 'log'       && <SyncLogTab log={syncLog} />}
        {tab === 'mapping'   && <MappingTab />}
      </div>
    </div>
  )
}

// ── Employees tab ──────────────────────────────────────────────────────────

function EmployeesTab({ rows, search, onSearch, total }: {
  rows: HaileyPerson[]; search: string; onSearch: (v: string) => void; total: number
}) {
  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 8 }}>
        <SearchInput value={search} onChange={onSearch} placeholder="Search employees…" width={260} />
        <div style={{ flex: 1 }} />
        <span className="muted tabular" style={{ fontSize: 11.5 }}>Showing {rows.length} of {total}</span>
        <button className="btn btn-ghost btn-sm"><Download size={13} />Export</button>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Title</th>
            <th>Department</th>
            <th>Reports to</th>
            <th style={{ width: 110 }}>Type</th>
            <th style={{ width: 80, textAlign: 'center' }}>Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.id}>
              <td>
                <div className="row" style={{ gap: 8 }}>
                  <PersonChip name={`${p.firstName} ${p.lastName}`} id={p.id} />
                  <span style={{ fontWeight: 500 }}>{p.firstName} {p.lastName}</span>
                </div>
              </td>
              <td className="muted" style={{ fontSize: 12 }}>{p.email ?? '—'}</td>
              <td className="muted">{p.jobTitle ?? '—'}</td>
              <td className="muted">{p.department ?? '—'}</td>
              <td className="muted">{p.reportsToName ?? '—'}</td>
              <td className="muted" style={{ fontSize: 12 }}>{fmtType(p.employmentType)}</td>
              <td style={{ textAlign: 'center' }}>
                {p.isActive
                  ? <CheckCircle size={14} style={{ color: 'var(--ns-ok)' }} />
                  : <XCircle    size={14} style={{ color: 'var(--ns-err)' }} />
                }
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 24 }}>
              {rows.length === 0 ? 'No employees synced yet — click Sync now' : 'No results'}
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ── Sync log tab ───────────────────────────────────────────────────────────

function SyncLogTab({ log }: { log: SyncLogEntry[] }) {
  const [entityFilter, setEntityFilter] = useState<string>('all')

  const entities = Array.from(new Set(log.map(l => l.entity))).sort()
  const filtered  = entityFilter === 'all' ? log : log.filter(l => l.entity === entityFilter)

  const grouped: Record<string, SyncLogEntry[]> = {}
  filtered.forEach(l => {
    const key = new Date(l.syncedAt).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(l)
  })

  const cutoff = Date.now() - 24 * 60 * 60 * 1000
  const last24 = log.filter(l => new Date(l.syncedAt).getTime() > cutoff)
  const successCount = last24.filter(l => l.status === 'success').length
  const successRate  = last24.length > 0 ? Math.round((successCount / last24.length) * 100) : 100

  const hourBuckets = Array.from({ length: 24 }, (_, i) => {
    const h = (new Date().getHours() - 23 + i + 24) % 24
    return last24.filter(l => new Date(l.syncedAt).getHours() === h && l.status === 'success').length
  })
  const maxBucket = Math.max(...hourBuckets, 1)

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
      {/* Main log */}
      <div className="card" style={{ padding: 0 }}>
        <div className="row" style={{ padding: '12px 14px', borderBottom: '1px solid var(--ns-border)', gap: 6 }}>
          <button className={`chip${entityFilter === 'all' ? ' active' : ''}`} onClick={() => setEntityFilter('all')}>All</button>
          {entities.map(e => (
            <button key={e} className={`chip${entityFilter === e ? ' active' : ''}`} onClick={() => setEntityFilter(e)}
              style={{ textTransform: 'capitalize' }}>{e}</button>
          ))}
          <div style={{ flex: 1 }} />
          <button className="btn btn-ghost btn-sm"><Download size={13} />Export log</button>
        </div>
        <div style={{ maxHeight: 540, overflow: 'auto' }}>
          {Object.entries(grouped).map(([minute, items]) => (
            <div key={minute}>
              <div style={{
                padding: '8px 14px', background: 'var(--ns-surface-2)',
                fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                color: 'var(--ns-text-muted)', borderTop: '1px solid var(--ns-border)',
                position: 'sticky', top: 0, zIndex: 1,
              }} className="tabular">{minute}</div>
              {items.map(l => (
                <div key={l.id} className="row" style={{ padding: '10px 14px', borderTop: '1px solid var(--ns-border)', gap: 12 }}>
                  {l.status === 'success'
                    ? <CheckCircle size={14} style={{ color: 'var(--ns-ok)', flexShrink: 0 }} />
                    : <AlertCircle size={14} style={{ color: 'var(--ns-err)', flexShrink: 0 }} />
                  }
                  <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 4, background: 'var(--ns-surface-2)', color: 'var(--ns-text-muted)', border: '1px solid var(--ns-border)', fontWeight: 500, minWidth: 80, textAlign: 'center', flexShrink: 0 }}>
                    {l.entity}
                  </span>
                  <span style={{ flex: 1, fontSize: 12.5 }}>
                    {l.recordsUpserted > 0
                      ? <>Synced <strong className="tabular">{l.recordsUpserted}</strong> records</>
                      : l.errorMessage
                        ? <span style={{ color: 'var(--ns-err)' }}>{l.errorMessage}</span>
                        : <span className="muted">No changes</span>
                    }
                  </span>
                  <span className="mono tabular muted" style={{ fontSize: 11 }}>
                    {new Date(l.syncedAt).toLocaleTimeString('sv-SE')}
                  </span>
                </div>
              ))}
            </div>
          ))}
          {Object.keys(grouped).length === 0 && (
            <div className="muted" style={{ textAlign: 'center', padding: 32 }}>No sync history yet</div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card" style={{ padding: 14 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Sync rate · last 24h</div>
          <div className="mono tabular" style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
            {successCount}<span style={{ fontSize: 13, color: 'var(--ns-text-muted)', fontWeight: 500 }}>/{last24.length || '—'}</span>
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{last24.length - successCount} failed · {successRate}% success</div>
          <div className="row" style={{ gap: 2, marginTop: 14, alignItems: 'flex-end', height: 32 }}>
            {hourBuckets.map((v, i) => (
              <div key={i} style={{
                flex: 1, borderRadius: 1,
                height: `${Math.max(10, (v / maxBucket) * 100)}%`,
                background: 'var(--ns-brand-strong)',
                opacity: 0.4 + (i / 24) * 0.6,
              }} />
            ))}
          </div>
          <div className="row" style={{ justifyContent: 'space-between', marginTop: 6, fontSize: 10, color: 'var(--ns-text-subtle)' }}>
            <span>24h ago</span><span>12h ago</span><span>now</span>
          </div>
        </div>

        <div className="card" style={{ padding: 14 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Endpoints</div>
          {(['employees'] as const).map(entity => {
            const entityLog = log.filter(l => l.entity === entity)
            const hasError  = entityLog.some(l => l.status === 'failed')
            return (
              <div key={entity} className="row" style={{ padding: '6px 0', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12.5, textTransform: 'capitalize' }}>{entity}</span>
                <span className={`dot ${hasError ? 'dot-warn' : 'dot-ok'}`} style={{ width: 6, height: 6 }} />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ── Field mapping tab ──────────────────────────────────────────────────────

function MappingTab() {
  const sections: { title: string; rows: [string, string][] }[] = [
    {
      title: 'Employees',
      rows: [
        ['employeeId',       'hailey_id'],
        ['firstName',        'first_name'],
        ['lastName',         'last_name'],
        ['email',            'email'],
        ['jobTitle',         'job_title'],
        ['department',       'department'],
        ['employmentStart',  'employment_start'],
        ['employmentEnd',    'employment_end'],
        ['employmentType',   'employment_type'],
        ['active',           'is_active'],
        ['managerId',        'manager_hailey_id'],
      ],
    },
  ]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {sections.map(s => (
        <div key={s.title} className="card" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14 }}>{s.title}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 24px 1fr', gap: '8px 12px', alignItems: 'center', maxWidth: 600 }}>
            <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ns-text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Hailey field</div>
            <div />
            <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--ns-text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Forge column</div>
            {s.rows.map(([from, to], i) => (
              <React.Fragment key={i}>
                <div className="mono" style={{ fontSize: 12, padding: '6px 10px', background: 'var(--ns-surface-2)', borderRadius: 6, border: '1px solid var(--ns-border)' }}>{from}</div>
                <div style={{ textAlign: 'center', color: 'var(--ns-text-subtle)', fontSize: 14 }}>→</div>
                <div className="mono" style={{ fontSize: 12, padding: '6px 10px', background: 'var(--ns-brand-soft)', borderRadius: 6, color: 'var(--ns-brand-strong)' }}>{to}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Shared helpers ─────────────────────────────────────────────────────────

function Breadcrumb() {
  return (
    <div className="row" style={{ gap: 4, marginBottom: 14, color: 'var(--ns-text-muted)', fontSize: 12 }}>
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Settings</Link>
      <ChevronRight size={11} />
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Integrations</Link>
      <ChevronRight size={11} />
      <span style={{ color: 'var(--ns-text)' }}>Hailey HR</span>
    </div>
  )
}

function DetailStat({ label, value, sub, trend, isCustom }: {
  label: string; value?: React.ReactNode; sub?: string
  trend?: number[]; isCustom?: boolean
}) {
  const max = trend ? Math.max(...(trend as number[]), 1) : 1
  return (
    <div className="card" style={{ padding: 16, position: 'relative', overflow: 'hidden' }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 500, marginBottom: 6, letterSpacing: '0.01em' }}>{label}</div>
      {isCustom
        ? <div style={{ marginTop: 2 }}>{value}</div>
        : <div className="mono tabular" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
      }
      {sub && <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>{sub}</div>}
      {trend && (
        <svg width="64" height="20" viewBox="0 0 64 20" style={{ position: 'absolute', bottom: 12, right: 12, opacity: 0.65 }}>
          <polyline fill="none" stroke="var(--ns-brand-strong)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
            points={trend.map((v, i) => `${i * (64 / (trend.length - 1))},${20 - (v / max) * 18}`).join(' ')} />
        </svg>
      )}
    </div>
  )
}

function PersonChip({ name, id }: { name: string; id: number }) {
  const initials = name.split(' ').filter(w => /^[A-Za-zÅÄÖåäö]/.test(w)).slice(0, 2).map(w => w[0]).join('').toUpperCase()
  const hue = (id * 83) % 360
  return (
    <div style={{
      width: 22, height: 22, borderRadius: 5, flexShrink: 0,
      background: `oklch(${0.55 + (id % 7) * 0.03} 0.12 ${hue})`,
      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 9.5, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
    }}>{initials.slice(0, 2)}</div>
  )
}

function SearchInput({ value, onChange, placeholder, width = 280 }: {
  value: string; onChange: (v: string) => void; placeholder: string; width?: number
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 10, height: 30, border: '1px solid var(--ns-border)', borderRadius: 6, background: 'var(--ns-surface)', width }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ns-text-subtle)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
      </svg>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ flex: 1, height: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: 13 }} />
    </div>
  )
}

function fmtType(t: string | null): string {
  if (!t) return '—'
  return t.replace(/([A-Z])/g, ' $1').trim()
}
